// Central native/web bridge. Everything Capacitor-specific is gated here so the
// web build behaves exactly as before and native code only runs on device.
import { Capacitor } from "@capacitor/core";

export const isNative = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch (e) {
    return false;
  }
};

export const platform = () => {
  try {
    return Capacitor.getPlatform();
  } catch (e) {
    return "web";
  }
};

// API/media origin. Native builds MUST hit the absolute production API (never the
// preview URL); web keeps using REACT_APP_BACKEND_URL.
export const apiBase = () => {
  if (isNative()) return process.env.REACT_APP_NATIVE_API_URL || "https://commandcenteradhd.com";
  return process.env.REACT_APP_BACKEND_URL;
};

// --- Key/value storage: Preferences on native, localStorage on web ---
export const kvGet = async (key) => {
  if (isNative()) {
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key });
    return value ?? null;
  }
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

export const kvSet = async (key, value) => {
  if (isNative()) {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({ key, value });
    return;
  }
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    /* quota / private mode */
  }
};

export const kvRemove = async (key) => {
  if (isNative()) {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.remove({ key });
    return;
  }
  try {
    localStorage.removeItem(key);
  } catch (e) {
    /* */
  }
};

// --- Native auth token (Bearer). Web keeps using the HttpOnly cookie. ---
const TOKEN_KEY = "cc_native_token";
let _tokenCache = null;
let _tokenLoaded = false;

export const loadAuthToken = async () => {
  if (!isNative()) return null;
  if (_tokenLoaded) return _tokenCache;
  _tokenCache = await kvGet(TOKEN_KEY);
  _tokenLoaded = true;
  return _tokenCache;
};

export const setAuthToken = async (token) => {
  _tokenCache = token;
  _tokenLoaded = true;
  if (isNative()) await kvSet(TOKEN_KEY, token);
};

export const clearAuthToken = async () => {
  _tokenCache = null;
  _tokenLoaded = true;
  if (isNative()) await kvRemove(TOKEN_KEY);
};

// Authorization header for native fetches (empty object on web).
export const authHeaders = async () => {
  if (!isNative()) return {};
  const t = await loadAuthToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

// --- Haptics: light tap on quick-win check (native only, silent on web) ---
export const hapticTap = async () => {
  if (!isNative()) return;
  try {
    const { Haptics, ImpactStyle } = await import("@capacitor/haptics");
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch (e) {
    /* */
  }
};

// --- Shell init: status bar style, splash hide, Android back button ---
export const initNativeShell = async (onBack) => {
  if (!isNative()) return () => {};
  const cleanups = [];
  try {
    const { StatusBar, Style } = await import("@capacitor/status-bar");
    await StatusBar.setStyle({ style: Style.Dark }); // dark = light text, matches navy header
    if (platform() === "android") {
      await StatusBar.setBackgroundColor({ color: "#0B132B" });
    }
  } catch (e) {
    /* */
  }
  try {
    const { SplashScreen } = await import("@capacitor/splash-screen");
    await SplashScreen.hide();
  } catch (e) {
    /* */
  }
  try {
    const { App } = await import("@capacitor/app");
    const handle = await App.addListener("backButton", ({ canGoBack }) => {
      if (typeof onBack === "function" && onBack(canGoBack)) return;
      if (canGoBack) {
        window.history.back();
      } else {
        App.exitApp();
      }
    });
    cleanups.push(() => handle.remove());
  } catch (e) {
    /* */
  }
  return () => cleanups.forEach((fn) => fn());
};
